import { action, computed, flow, IObservableArray, observable, toJS } from 'mobx';
import type RootStore from 'stores/RootStore';
import type { OrderBy, ScriptCounts, WhereFilter } from 'types/common';
import Query from 'utilities/Query';
import Reminder from 'stores/Reminder';

export default class Reminders {
	rootStore: RootStore = null;

	constructor(rootStore) {
		this.rootStore = rootStore;
	}

	@observable pageSize: number = 100;
	@observable items: IObservableArray<Reminder> = [];
	@observable pageIndex: number = 0;
	@observable itemCount: number = 0;
	@observable filters: IObservableArray<WhereFilter> = [];
	@observable orderBy: OrderBy = { field: 'due', asc: true };
	@observable loadingCounter: number = 0;
	@observable practiceID: number = 0;

	extend(data: JSON) {
		Object.assign(this, data);
	}

	loadItems = flow(
		function*() {
			let filters: Array<WhereFilter> = [];
			if (this.practiceID) {
				filters.push({ field: 'searchPracticeID', value: this.practiceID });
			}
			filters = filters.concat(toJS(this.filters));
			try {
				this.loadingCounter++;
				let query = new Query(filters, this.orderBy, this.pageIndex, this.pageSize);
				let res = { items: [], itemCount: 0 };
				res = yield Reminder.loadReminders(query);
				this.items = res.items;
				this.itemCount = res.itemCount;
			} finally {
				this.loadingCounter--;
			}
		}.bind(this)
	);

	@action
	reload = () => {
		this.loadItems();
	};

	@action
	setPracticeID(value) {
		this.practiceID = value;
		this.pageIndex = 0;
		this.loadItems();
	}

	@action
	setPageIndex(idx: number) {
		this.pageIndex = idx;
		this.loadItems();
	}

	@action
	setPageSize(idx: number) {
		this.pageSize = idx;
		this.loadItems();
	}

	@action
	addFilter(filter: WhereFilter) {
		this.filters = this.filters.filter(f => f.field !== filter.field);
		this.filters.push(filter);
		this.pageIndex = 0;
		this.loadItems();
	}

	@action
	removeFilter(field: string) {
		let fieldToRemove = this.filters.find(el => el.field === field);
		if (fieldToRemove) {
			this.filters.remove(fieldToRemove);
			this.pageIndex = 0;
			this.loadItems();
		}
	}

	@action
	setOrderBy(field: string) {
		if (this.orderBy.field === field) {
			this.orderBy.asc = !this.orderBy.asc;
		} else {
			this.orderBy.field = field;
			this.orderBy.asc = true;
		}
		this.loadItems();
	}

	@computed
	get isLoading(): boolean {
		return this.loadingCounter > 0;
	}

	@computed
	get numberOfPages(): number {
		return Math.ceil(this.itemCount / this.pageSize);
	}
}



// WEBPACK FOOTER //
// ./src/stores/ui/manager/Reminders.js